import { useQuery } from '@tanstack/react-query';
import { CalendarCheck2, Check, CircleAlert, ShieldAlert, TimerOff, TriangleAlert } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ErrorCodes, errorCode, errorText } from '../../components/ErrorToast';
import { Badge, Button, Callout, Field, Skeleton, inputCls } from '../../components/ui';
import { ApiError } from '../../lib/errors';
import {
  type InteractionState,
  interactionAbort,
  interactionConsent,
  interactionLogin,
  interactionState,
  isExpired,
} from './api';
import { LABEL, T, TITLE, say } from './texts';

/*
 * Страница входа и согласия ИИ-агента `/oauth/interaction/:uid` (005, T029; research R15; contracts/oauth.md;
 * FR-005, FR-060, FR-063). Сервер авторизации переводит сюда браузер, когда клиент агента начинает вход. Экраны:
 * - «вход» — логин и пароль табеля (`POST …/login`), ошибки — как на странице входа табеля;
 * - «согласие» — клиент, хост его документа, хост адреса возврата (для этого компьютера — предупреждение), права токена
 *   с сервера — только их тексты, без кодов; «Разрешить» (`POST …/consent`) и «Отказать» (`POST …/abort`);
 * - ошибки — слова сервера для кодов 005 (`errorText`), код ответа — рядом с кодом ошибки (`errorCode`);
 * - согласие невозможно: нет права «ИИ-агент» (FR-063), нет роли или нет прав для токена — причина и только «Отказать»;
 * - загрузка, ошибка сети с «Повторить», взаимодействие истекло (404).
 * После ответа браузер уходит по `redirectTo` через `location.assign`: это обычный переход, CSP `form-action` его не
 * касается (research R15). Один интерфейс на 375/768/1280 px: карточка до 480 px по центру, на телефоне — во всю
 * ширину с полями 16 px; кнопки и поля на телефоне — 40 px.
 */

export function OAuthInteractionPage() {
  const uid = useParams().uid ?? '';
  const state = useQuery({ queryKey: ['oauth-interaction', uid], queryFn: () => interactionState(uid) });
  // 404 на «Войти», «Разрешить» или «Отказать»: взаимодействие истекло, пока страница была открыта.
  const [expired, setExpired] = useState(false);
  const onExpired = () => setExpired(true);

  const data = state.data;
  let body: ReactNode;
  let footer: string | undefined;
  if (expired || isExpired(state.error)) body = <Expired />;
  else if (state.isError)
    body = <LoadError error={state.error} busy={state.isFetching} onRetry={() => void state.refetch()} />;
  else if (!data) body = <Loading />;
  else if (data.step === 'login') {
    body = <LoginStep uid={uid} s={data} onLoggedIn={() => state.refetch()} onExpired={onExpired} />;
    footer = T.accountNote;
  } else if (refusal(data).length > 0) body = <Refused uid={uid} s={data} onExpired={onExpired} />;
  else body = <Consent uid={uid} s={data} onExpired={onExpired} />;

  return (
    <main className="flex min-h-dvh items-center justify-center bg-gradient-to-br from-hero-a via-canvas-2 to-hero-b px-4 py-8">
      <div className="w-full max-w-[480px]">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-control bg-accent text-white">
            <CalendarCheck2 size={20} strokeWidth={1.75} aria-hidden />
          </span>
          <span className="text-[20px] font-semibold tracking-[-0.01em]">{LABEL.brand}</span>
        </div>
        <section className="rounded-sheet border border-line bg-surface p-7 shadow-pop max-sm:p-5">
          {body}
        </section>
        {footer && <p className="mt-5 text-center text-[12px] text-ink-3">{footer}</p>}
      </div>
    </main>
  );
}

/**
 * Заголовок экрана. При показе фокус — на заголовок: экран сменился (вход → согласие), а поля для фокуса нет.
 * На экране входа фокус — на поле «Логин» (`focus={false}`).
 */
function Heading({ children, focus = true }: { children: string; focus?: boolean }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focus) ref.current?.focus();
  }, [focus]);
  return (
    <h1 ref={ref} tabIndex={-1} className="text-[18px] font-semibold outline-none">
      {children}
    </h1>
  );
}

/** Сведения «подпись — значение»: клиент, документ клиента, адрес возврата. На телефоне значение переносится вниз. */
function Facts({ items }: { items: readonly { label: string; value: ReactNode }[] }) {
  return (
    <dl className="mt-5 divide-y divide-line rounded-card border border-line">
      {items.map((f) => (
        <div
          key={f.label}
          className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-2.5"
        >
          <dt className="text-[13px] text-ink-2">{f.label}</dt>
          <dd className="min-w-0 text-[14px] break-words">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

const host = (h: string) => <span className="font-mono text-[13px] break-all">{h}</span>;

const clientFacts = (s: InteractionState) => [
  { label: LABEL.client, value: <span className="font-semibold">{s.client.name}</span> },
  { label: LABEL.clientDoc, value: host(s.client.host) },
];

/**
 * Ошибка под формой или кнопками: текст и код ошибки (requestId) для команды разработки. Если текст — слова сервера,
 * рядом с кодом ошибки — код ответа (`errorCode`, итоговое ревью T160).
 */
function ErrorBox({
  id,
  text,
  requestId,
  code = null,
}: { id?: string; text: string; requestId: string | null; code?: string | null }) {
  return (
    <p id={id} role="alert" className="rounded-control bg-bad-soft px-3 py-2 text-[13px] text-bad">
      {text}
      {(requestId || code) && (
        <span className="mt-0.5 block text-[12px]">
          {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
        </span>
      )}
    </p>
  );
}

/** Кнопки экрана справа, главная — последней; на телефоне делят ширину поровну. */
function Actions({ children }: { children: ReactNode }) {
  return <div className="mt-6 flex justify-end gap-2 max-sm:*:flex-1">{children}</div>;
}

/* ---------- Вход ---------- */

interface LoginError {
  text: string;
  requestId: string | null;
  /** Код ответа рядом с кодом ошибки, если текст — слова сервера. */
  code: string | null;
  /** Ошибка в логине или пароле: поля помечаются `aria-invalid`. */
  fields: boolean;
}

/** Ошибки входа — как на странице входа табеля (`pages/Login`). */
function loginError(err: unknown): LoginError {
  if (err instanceof ApiError && err.code === 'LOGIN_LOCKED')
    return { text: say.locked(err.retryAfterSec), requestId: null, code: null, fields: false };
  if (err instanceof ApiError && err.code === 'BAD_CREDENTIALS')
    return { text: T.badCredentials, requestId: null, code: null, fields: true };
  return {
    text: errorText(err),
    requestId: err instanceof ApiError ? err.requestId : null,
    code: errorCode(err),
    fields: false,
  };
}

function LoginStep({
  uid,
  s,
  onLoggedIn,
  onExpired,
}: {
  uid: string;
  s: InteractionState;
  /** Загрузить шаг заново: после входа сервер отдаёт «согласие». */
  onLoggedIn: () => Promise<unknown>;
  onExpired: () => void;
}) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<LoginError | null>(null);
  const [busy, setBusy] = useState(false);
  const loginRef = useRef<HTMLInputElement>(null);
  const errorId = useId();
  // «Отказать» и до входа: клиент агента сразу получает отказ, а не ждёт своего таймаута.
  const deny = useDecision(uid, onExpired);

  useEffect(() => {
    loginRef.current?.focus();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await interactionLogin(uid, login.trim(), password);
      await onLoggedIn();
    } catch (err) {
      if (isExpired(err)) return onExpired();
      setError(loginError(err));
      // Введённое остаётся, фокус — на первое поле формы.
      loginRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  const fieldAria = {
    'aria-invalid': error?.fields ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
  };
  return (
    <form onSubmit={submit}>
      <Heading focus={false}>{TITLE.login}</Heading>
      <p className="mt-1 text-[13px] text-ink-2">{T.loginIntro}</p>
      <Facts items={clientFacts(s)} />
      <div className="mt-5">
        <Field label={LABEL.login}>
          <input
            ref={loginRef}
            className={inputCls}
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
            required
            {...fieldAria}
          />
        </Field>
        <Field label={LABEL.password}>
          <input
            className={inputCls}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            {...fieldAria}
          />
        </Field>
      </div>
      {error && (
        <div className="mb-4">
          <ErrorBox id={errorId} text={error.text} requestId={error.requestId} code={error.code} />
        </div>
      )}
      <Button type="submit" variant="primary" size="lg" className="mt-1 w-full" disabled={busy}>
        {busy ? LABEL.entering : LABEL.enter}
      </Button>
      <div className="mt-2 grid">{deny.button('deny', 'secondary')}</div>
      {deny.errorBox}
    </form>
  );
}

/* ---------- Согласие ---------- */

type Decision = 'allow' | 'deny';

/** «Разрешить» и «Отказать»: ответ сервера — `redirectTo`, браузер уходит к клиенту агента. */
function useDecision(uid: string, onExpired: () => void) {
  const [busy, setBusy] = useState<Decision | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  async function decide(d: Decision) {
    if (busy) return;
    setBusy(d);
    setError(null);
    try {
      const to = await (d === 'allow' ? interactionConsent(uid) : interactionAbort(uid));
      // Кнопки остаются недоступными: страница уходит по адресу возврата.
      window.location.assign(to);
    } catch (err) {
      if (isExpired(err)) return onExpired();
      setError(err instanceof ApiError ? err : new ApiError(0, 'INTERNAL', 'Ошибка сервера', null));
      setBusy(null);
    }
  }
  const button = (d: Decision, variant: 'primary' | 'secondary') => (
    <Button size="lg" variant={variant} disabled={busy !== null} onClick={() => void decide(d)}>
      {busy === d ? LABEL.sending : d === 'allow' ? LABEL.allow : LABEL.deny}
    </Button>
  );
  const errorBox = error && (
    <div className="mt-4">
      <ErrorBox text={errorText(error)} requestId={error.requestId} code={errorCode(error)} />
    </div>
  );
  return { button, errorBox };
}

/** Почему согласие невозможно; пусто — возможно. Без прав для токена «Разрешить» ничего не дало бы. */
function refusal(s: InteractionState): { text: string; fix?: string }[] {
  const out: { text: string; fix?: string }[] = [];
  if (s.rightMissing) out.push({ text: T.rightMissing, fix: T.rightMissingFix });
  if (s.noRole) out.push({ text: T.noRole, fix: T.noRoleFix });
  if (out.length === 0 && s.scopes.length === 0) out.push({ text: T.noScopes });
  return out;
}

interface StepProps {
  uid: string;
  s: InteractionState;
  onExpired: () => void;
}

function Consent({ uid, s, onExpired }: StepProps) {
  const { button, errorBox } = useDecision(uid, onExpired);
  const scopesId = useId();
  const redirect = (
    <span className="inline-flex flex-wrap items-center gap-2">
      {host(s.redirectHost)}
      {s.isLocalhost && (
        <Badge tone="warn" dot>
          {LABEL.thisComputer}
        </Badge>
      )}
    </span>
  );
  return (
    <>
      <Heading>{TITLE.consent}</Heading>
      <p className="mt-1 text-[13px] text-ink-2">{T.consentIntro}</p>
      {/* Учётная запись — чтобы в общем браузере было видно, от чьего имени подключается агент (ревью T160). */}
      <Facts
        items={[
          ...(s.account ? [{ label: LABEL.account, value: s.account.login }] : []),
          ...clientFacts(s),
          { label: LABEL.redirect, value: redirect },
        ]}
      />
      {s.isLocalhost && (
        <div className="mt-3">
          <Callout tone="warn" icon={TriangleAlert}>
            {T.localhost}
          </Callout>
        </div>
      )}
      <h2 id={scopesId} className="mt-6 text-[14px] font-semibold">
        {LABEL.scopes}
      </h2>
      <ul aria-labelledby={scopesId} className="mt-2 space-y-2">
        {/* Только текст права: код (`rules:read`) — для разработчика, на экране его нет (итоговое ревью T160). */}
        {s.scopes.map((sc) => (
          <li key={sc.scope} className="flex items-start gap-2.5 text-[14px]">
            <Check size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ok" aria-hidden />
            <span className="min-w-0">{sc.text}</span>
          </li>
        ))}
      </ul>
      {errorBox}
      <Actions>
        {button('deny', 'secondary')}
        {button('allow', 'primary')}
      </Actions>
    </>
  );
}

function Refused({ uid, s, onExpired }: StepProps) {
  const { button, errorBox } = useDecision(uid, onExpired);
  return (
    <>
      <Heading>{TITLE.refused}</Heading>
      <Facts items={clientFacts(s)} />
      <div className="mt-4">
        <Callout tone="bad" icon={ShieldAlert}>
          <div className="space-y-2">
            {refusal(s).map((r) => (
              <div key={r.text}>
                <p>{r.text}</p>
                {r.fix && <p className="text-ink-2">{r.fix}</p>}
              </div>
            ))}
          </div>
        </Callout>
      </div>
      <p className="mt-4 text-[13px] text-ink-2">{T.refuseNote}</p>
      {errorBox}
      <Actions>{button('deny', 'primary')}</Actions>
    </>
  );
}

/* ---------- Состояния экрана ---------- */

function Loading() {
  return (
    <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
      <Skeleton className="h-6 w-3/5" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="mt-5 h-[84px] w-full" />
      <Skeleton className="mt-5 h-4 w-2/5" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="mt-6 h-10 w-full" />
    </div>
  );
}

function Notice({
  icon,
  tone,
  title,
  children,
}: { icon: ReactNode; tone: 'neutral' | 'bad'; title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center py-4 text-center">
      <span
        className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-card ${tone === 'bad' ? 'bg-bad-soft text-bad' : 'bg-subtle text-ink-2'}`}
      >
        {icon}
      </span>
      <Heading>{title}</Heading>
      {children}
    </div>
  );
}

function Expired() {
  return (
    <Notice icon={<TimerOff size={20} strokeWidth={1.75} aria-hidden />} tone="neutral" title={TITLE.expired}>
      <p className="mt-1 text-[13px] text-ink-2">{T.expired}</p>
    </Notice>
  );
}

function LoadError({ error, busy, onRetry }: { error: Error; busy: boolean; onRetry: () => void }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <Notice
      icon={<CircleAlert size={20} strokeWidth={1.75} aria-hidden />}
      tone="bad"
      title={TITLE.loadError}
    >
      <p className="mt-1 text-[13px] text-ink-2">{errorText(error)}</p>
      {(requestId || code) && (
        <p className="mt-2 text-[12px] text-ink-3">
          {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} className="text-ink-2" />
        </p>
      )}
      <Button variant="primary" size="lg" className="mt-5" disabled={busy} onClick={onRetry}>
        {LABEL.retry}
      </Button>
    </Notice>
  );
}
